import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface CampSearchInputProps {
  placeholder?: string;
  className?: string;
  onCampSelect?: (camp: any) => void; // Optional callback for camp selection
}

export default function CampSearchInput({ 
  placeholder = "Search by camp name...", 
  className = "",
  onCampSelect
}: CampSearchInputProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch camps based on search query
  const { data: camps = [], isLoading } = useQuery(
    ['camp-search', query],
    async () => {
      if (query.length < 2) return [];
      
      const { data, error } = await supabase
        .from('camps')
        .select('id, name, slug, city, state')
        .ilike('name', `%${query}%`)
        .eq('active', true)
        .limit(10);
      
      if (error) throw error;
      return data || [];
    },
    {
      enabled: query.length >= 2,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length >= 2);
    setSelectedIndex(-1);
  };

  // Handle camp selection
  const handleCampSelect = (camp: any) => {
    setQuery('');
    setIsOpen(false);
    
    if (onCampSelect) {
      // Use callback if provided (for MyCampTeaserPage)
      onCampSelect(camp);
    } else {
      // Default behavior: navigate to camp page
      navigate(`/camps/${camp.slug}`);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || camps.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < camps.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < camps.length) {
          handleCampSelect(camps[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          placeholder={placeholder}
          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Dropdown Results */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
        >
          {isLoading ? (
            <div className="px-4 py-2 text-gray-500 text-sm">
              Searching camps...
            </div>
          ) : camps.length > 0 ? (
            camps.map((camp, index) => (
              <button
                key={camp.id}
                onClick={() => handleCampSelect(camp)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                  index === selectedIndex ? 'bg-gray-100' : ''
                }`}
              >
                <div className="font-medium text-gray-900">{camp.name}</div>
                <div className="text-gray-500 text-xs">
                  {camp.city}, {camp.state}
                </div>
              </button>
            ))
          ) : query.length >= 2 ? (
            <div className="px-4 py-2 text-gray-500 text-sm">
              No camps found matching "{query}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

